import { TextField } from '@mui/material';
import React, { ChangeEvent, FC, KeyboardEvent, useRef, useState } from 'react';

import { CHECKLIST__MAX_TEXT_LENGTH } from '../../../../constants/forms';
import useIsMobile from '../../../../hooks/useIsMobile';
import translate from '../../../../i18n/translate';
import { Text } from '../../../../models/checklist';
import MuiIcon from '../../../MUI/MuiIcon';

import * as Styled from './styled';

export type Props = {
  requestCreate: (text: Text) => void;
};

/**
 * Checklist item add input
 *
 * @param props               Props passed to the component
 * @param props.requestCreate Request that a new checklist item is added
 * @returns                   The component itself
 */
const ChecklistCreateInput: FC<Props> = ({ requestCreate }) => {
  const isMobile = useIsMobile();
  const [value, setValue] = useState('');
  const inputRef = useRef<HTMLInputElement | null>(null);

  const createButtonLabel = translate('GENERAL__DONE');
  const inputLabel = translate('CHECKLISTS__NEW_ITEM');
  const placeholder = translate('CHECKLIST__ADD__PLACEHOLDER');
  const canCreate = value.length > 0;

  /**
   * Handle changing of input value
   *
   * @param event The event that took place
   */
  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setValue(event.target.value);
  };

  /**
   * The user has requested to add new item
   */
  const handleCreate = () => {
    requestCreate(value);
    setValue('');
    inputRef.current?.focus();
  };

  /**
   * Handle key down event to ensure creating on Enter
   *
   * @param event The event that took place
   */
  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && canCreate) {
      event.preventDefault();
      handleCreate();
    }
  };

  return (
    <TextField
      autoFocus={true}
      fullWidth={true}
      inputRef={inputRef}
      label={inputLabel}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      placeholder={placeholder}
      slotProps={{
        htmlInput: {
          maxLength: CHECKLIST__MAX_TEXT_LENGTH,
        },
        input: {
          endAdornment: canCreate && (
            <Styled.CreateButton
              onClick={handleCreate}
              startIcon={!isMobile && <MuiIcon icon="check" />}
            >
              {createButtonLabel}
            </Styled.CreateButton>
          ),
          startAdornment: <MuiIcon icon="add" />,
        },
      }}
      value={value}
    />
  );
};

export default ChecklistCreateInput;
